// ---------------------------------------------------------------------------
// Sprint 16 A3: Dashboard "Newspaper" Layout
// ---------------------------------------------------------------------------
// CSS Grid: banner → score+priorities → metrics → insights+activity
// Responsive: single column <1024px

import { useAuth } from "@/lib/auth-provider";
import { EaseScoreGauge } from "@/components/dashboard/ease-score-gauge";
import { KeyMetricsRow } from "@/components/dashboard/key-metrics-row";
import { AiPrioritiesList } from "@/components/dashboard/ai-priorities-list";
import { AiInsightsCard } from "@/components/dashboard/ai-insights-card";
import { RecentActivityTimeline } from "@/components/dashboard/recent-activity-timeline";
import { SiteHealthWidget } from "@/components/dashboard/site-health-widget";
import { useDashboardMetrics } from "@/hooks/use-dashboard-metrics";
import { useEaseScore } from "@/hooks/use-ease-score";
import { useBriefingPriorities } from "@/hooks/use-briefing-priorities";
import { useRecentActivity } from "@/hooks/use-recent-activity";
import { useIntelligentDensity } from "@/hooks/use-intelligent-density";
import { useDensity, type Density } from "@/providers/density-context";
import { Skeleton } from "@/components/skeleton";
import { ArrowRight, Sparkles, X } from "lucide-react";

export default function DashboardPage() {
  const { user } = useAuth();
  const metrics = useDashboardMetrics();
  const easeScore = useEaseScore();
  const priorities = useBriefingPriorities();
  const activity = useRecentActivity();
  const { global, setGlobal } = useDensity();
  const { suggestion, dismiss } = useIntelligentDensity(global);

  const greeting = getGreeting();
  const firstName = user?.name?.split(" ")[0] ?? "there";
  const suggestedDensityLabel = getDensityLabel(suggestion.suggestedDensity);
  const currentDensityLabel = getDensityLabel(suggestion.currentDensity);

  const handleAcceptDensitySuggestion = () => {
    setGlobal(suggestion.suggestedDensity as Density);
    dismiss();
  };

  return (
    <div className="density-gap max-w-7xl mx-auto px-4 py-6 space-y-6">
      {/* Banner — brand gradient with AI headline (A13) */}
      <div className="stagger-item bg-gradient-brand rounded-xl px-6 py-5 text-white shadow-lg card-hover">
        <p className="text-sm text-white/70">{greeting}, {firstName}</p>
        <h1 className="text-xl font-bold mt-1">
          {priorities.data && priorities.data.length > 0
            ? `${priorities.data.length} priorities today — ${priorities.data[0]?.title ?? "focus on what matters"}`
            : "You're all caught up — great work!"}
        </h1>
      </div>

      {suggestion.shouldSuggest ? (
        <section className="stagger-item density-card rounded-xl border border-[var(--color-border)] bg-[var(--color-card)] p-5 shadow-sm">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
            <div className="flex gap-3">
              <div className="mt-0.5 rounded-full bg-[var(--color-primary)]/10 p-2 text-[var(--color-primary)]">
                <Sparkles className="h-4 w-4" />
              </div>
              <div className="space-y-1.5">
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[var(--color-primary)]">
                  Workspace Fit
                </p>
                <h2 className="text-base font-semibold text-[var(--color-foreground)]">
                  Recommended density: {suggestedDensityLabel}
                </h2>
                <p className="density-text text-sm text-[var(--color-muted-foreground)]">
                  {suggestion.reason}
                </p>
                <p className="text-xs text-[var(--color-muted-foreground)]">
                  You are currently on {currentDensityLabel}. This update keeps more live context visible without leaving the dashboard.
                </p>
              </div>
            </div>

            <div className="flex flex-col gap-2 sm:flex-row lg:flex-col lg:items-end">
              <button
                type="button"
                data-density-action="accept"
                onClick={handleAcceptDensitySuggestion}
                className="inline-flex items-center justify-center gap-2 rounded-lg bg-[var(--color-primary)] px-4 py-2 text-sm font-medium text-[var(--color-primary-foreground,var(--color-background))] transition-colors hover:bg-[var(--color-primary)]/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-primary)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--color-card)] active:scale-[0.99]"
              >
                Switch to {suggestedDensityLabel}
                <ArrowRight className="h-4 w-4" />
              </button>
              <button
                type="button"
                data-density-action="dismiss"
                onClick={dismiss}
                className="inline-flex items-center justify-center gap-2 rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] px-4 py-2 text-sm font-medium text-[var(--color-foreground)] transition-colors hover:bg-[var(--color-surface)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-primary)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--color-card)] active:bg-[var(--color-surface)]"
              >
                Keep {currentDensityLabel}
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
        </section>
      ) : null}

      {/* Key Metrics Row */}
      <KeyMetricsRow metrics={metrics.data} isLoading={metrics.isLoading} />

      {/* Main Grid: Score+Priorities | Insights+Activity */}
      <div className="density-gap grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left column: Score + Priorities (2/3 width) */}
        <div className="lg:col-span-2 space-y-6">
          {/* Score + Priorities row */}
          <div className="density-gap grid grid-cols-1 md:grid-cols-[auto_1fr] gap-6">
            {/* Ease Score */}
            <div className="stagger-item density-card card-hover rounded-lg border border-[var(--color-border)] bg-[var(--color-card)] p-6 flex items-center justify-center shadow-sm">
              {easeScore.isLoading || !easeScore.data ? (
                <Skeleton className="h-32 w-32 rounded-full" />
              ) : (
                <EaseScoreGauge
                  score={easeScore.data.score}
                  trend={easeScore.data.trend}
                  change={easeScore.data.change}
                />
              )}
            </div>

            {/* Today's Priorities */}
            <div>
              <h2 className="text-lg font-semibold text-[var(--color-foreground)] mb-3">Today's Priorities</h2>
              <AiPrioritiesList priorities={priorities.data} isLoading={priorities.isLoading} />
            </div>
          </div>

          {/* Site Health Widget (PB-T.31) */}
          <SiteHealthWidget />
        </div>

        {/* Right column: Insights + Activity (1/3 width) */}
        <div className="space-y-6">
          <AiInsightsCard />

          <div className="stagger-item density-card card-hover rounded-lg border border-[var(--color-border)] bg-[var(--color-card)] p-5 shadow-sm">
            <h3 className="text-sm font-semibold text-[var(--color-foreground)] mb-3">Recent Activity</h3>
            <RecentActivityTimeline activities={activity.data} isLoading={activity.isLoading} />
          </div>
        </div>
      </div>
    </div>
  );
}

function getGreeting(): string {
  const hour = new Date().getHours();
  if (hour < 12) return "Good morning";
  if (hour < 18) return "Good afternoon";
  return "Good evening";
}

function getDensityLabel(density: string): string {
  switch (density) {
    case "easy":
      return "Easy";
    case "power":
      return "Power";
    default:
      return "Normal";
  }
}
